import React, { useEffect, useState } from 'react';
import { X, CreditCard, Calendar, DollarSign, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
interface BillingManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: 'pay-per-scan' | 'pro';
}
interface BillingHistory {
  date: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  description: string;
}
export function BillingManagementModal({
  isOpen,
  onClose,
  currentPlan
}: BillingManagementModalProps) {
  const {
    user
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [nextBillingDate, setNextBillingDate] = useState<string>('');
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchBillingData();
    }
  }, [isOpen, user?.id]);
  const fetchBillingData = async () => {
    setLoading(true);
    try {
      // Fetch billing history from backend
      const response = await fetch(`https://project-observant-artichoke-789.magicpatterns.app/functions/v1/billing-history?user_id=${user?.id}`);
      if (response.ok) {
        const data = await response.json();
        setBillingHistory(data.history || []);
        setNextBillingDate(data.next_billing_date || '');
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      // Set mock data for demo purposes
      setBillingHistory([{
        date: new Date().toISOString(),
        amount: currentPlan === 'pro' ? 9.99 : 2.99,
        status: 'paid',
        description: currentPlan === 'pro' ? 'Pro Plan - Monthly' : 'Single Scan'
      }]);
      // Calculate next billing date (30 days from now for Pro)
      if (currentPlan === 'pro') {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + 30);
        setNextBillingDate(nextDate.toLocaleDateString());
      }
    } finally {
      setLoading(false);
    }
  };
  const handleUpdatePaymentMethod = () => {
    // Redirect to Paystack customer portal or payment update page
    window.open('https://paystack.com/pay/update-payment', '_blank');
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-1720">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8" data-id="element-1721">
        <div className="flex items-center justify-between mb-6" data-id="element-1722">
          <h2 className="text-2xl font-bold" data-id="element-1723">Billing Management</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1724">
            <X size={24} data-id="element-1725" />
          </button>
        </div>

        {/* Current Plan Summary */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6" data-id="element-1726">
          <div className="flex items-center justify-between mb-2" data-id="element-1727">
            <h3 className="font-semibold text-purple-900" data-id="element-1728">Current Plan</h3>
            <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full font-medium" data-id="element-1729">
              Active
            </span>
          </div>
          <p className="text-2xl font-bold text-purple-900 mb-1" data-id="element-1730">
            {currentPlan === 'pro' ? 'Pro Plan' : 'Pay per Scan'}
          </p>
          <p className="text-purple-700" data-id="element-1731">
            ${currentPlan === 'pro' ? '9.99/month' : '2.99/scan'}
          </p>
        </div>

        {/* Next Billing Date (Pro only) */}
        {currentPlan === 'pro' && nextBillingDate && <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-id="element-1732">
            <div className="flex items-center" data-id="element-1733">
              <Calendar size={20} className="text-gray-600 mr-3" data-id="element-1734" />
              <div data-id="element-1735">
                <p className="text-sm text-gray-600" data-id="element-1736">Next Billing Date</p>
                <p className="font-semibold" data-id="element-1737">{nextBillingDate}</p>
              </div>
            </div>
          </div>}

        {/* Payment Method */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6" data-id="element-1738">
          <div className="flex items-center justify-between mb-3" data-id="element-1739">
            <div className="flex items-center" data-id="element-1740">
              <CreditCard size={20} className="text-gray-600 mr-3" data-id="element-1741" />
              <div data-id="element-1742">
                <p className="font-semibold" data-id="element-1743">Payment Method</p>
                <p className="text-sm text-gray-600" data-id="element-1744">Managed via Paystack</p>
              </div>
            </div>
            <button onClick={handleUpdatePaymentMethod} className="flex items-center text-purple-600 hover:text-purple-700 font-medium text-sm" data-id="element-1745">
              Update
              <ExternalLink size={16} className="ml-1" data-id="element-1746" />
            </button>
          </div>
        </div>

        {/* Billing History */}
        <div className="mb-6" data-id="element-1747">
          <h3 className="font-semibold mb-3" data-id="element-1748">Billing History</h3>
          {loading ? <div className="text-center py-8 text-gray-500" data-id="element-1749">
              Loading billing history...
            </div> : billingHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-1750">
              {billingHistory.map((item, index) => <div key={index} className="flex items-center justify-between p-4 border-b border-gray-100 last:border-b-0" data-id="element-1751">
                  <div className="flex items-center" data-id="element-1752">
                    <DollarSign size={20} className="text-gray-400 mr-3" data-id="element-1753" />
                    <div data-id="element-1754">
                      <p className="font-medium text-sm" data-id="element-1755">{item.description}</p>
                      <p className="text-xs text-gray-500" data-id="element-1756">
                        {new Date(item.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1757">
                    <p className="font-semibold" data-id="element-1758">${item.amount.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'paid' ? 'bg-green-100 text-green-700' : item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-1759">
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                </div>)}
            </div> : <div className="text-center py-8 text-gray-500" data-id="element-1760">
              No billing history available
            </div>}
        </div>

        <button onClick={onClose} className="w-full bg-gray-900 text-white rounded-lg py-3 font-medium hover:bg-gray-800 transition-colors" data-id="element-1761">
          Close
        </button>
      </div>
    </div>;
}